import { localityDecoder } from '@/common/LocalityDecoder';
import {venueDecoder} from '@/common/VenueDecoder';
const TULUM_PARTY_BASE_URL = "https://www.tulum.party";
const SPOTIFY_ARTIST_BASE_URL = "https://open.spotify.com/artist/"; // Confirmed correct Spotify base
const TULUM_TIMEZONE_OFFSET = "-05:00"; // Quintana Roo is UTC-5 year-round
export function artistSchemaGenerator(artist, parties) {
  // Helper function to safely get nested properties
  const getSafe = (fn, defaultValue = undefined) => {
    try {
      return fn();
    } catch (e) {
      return defaultValue;
    }
  };

  // Cleans and truncates content for the description
  function cleanContent() {
    const shortDescription = getSafe(() => artist.metadata.short);
    if (!shortDescription) {
      return undefined; // Or a default description if you prefer
    }
    // Remove HTML tags, replace non-breaking spaces, then trim
    const cleaned = shortDescription
      .replace(/(<([^>]+)>)/gi, "")
      .replace(/\u00A0/gi, " ") // \u00A0 is the non-breaking space
      .trim();

    // Truncate and add ellipsis
    if (cleaned.length > 250) {
      return cleaned.substring(0, 250) + "…"; // substring is generally preferred over substr
    }
    return cleaned;
  }

  function location(title) {

    let locality = localityDecoder(title);
    let venue = venueDecoder(title);
    if(!venue) {
      return {};
    }
    return {
      "@type": "NightClub",
      "name": venue.name,
      "url":  venue.slug && (TULUM_PARTY_BASE_URL + venue.slug),
      "address": {
        "@type": "PostalAddress",
        "streetAddress": venue?.directions?.address, // REQUIRED - Extracted from text
        "addressLocality": locality, // REQUIRED - Extracted from text
        "postalCode": venue?.directions?.postalCode, // REQUIRED - Extracted from text
        "addressRegion": venue?.directions?.addressRegion, // REQUIRED - Extracted from text (Quintana Roo)
        "addressCountry": "MX"
      }
    }
  }

  // Generates structured data for upcoming parties/events
  function generateEventData() {
    if (!parties || !Array.isArray(parties)) {
      return undefined;
    }
    return parties.map((party) => {
      // Ensure essential party data exists
      const title = getSafe(() => party.title);
      const slug = getSafe(() => party.slug);
      const date = getSafe(() => party.metadata.date);
      const startTime = getSafe(() => party.metadata.start_time);

      if (!title || !slug || !date || !startTime) {
        console.warn("Skipping party due to missing essential data:", party);
        return null; // Will be filtered out later
      }

      return {
        "@type": "MusicEvent",
        // Use the base URL constant
        "@id": `${TULUM_PARTY_BASE_URL}/event/${slug}#entity`,
        name: title,
        url: `${TULUM_PARTY_BASE_URL}/event/${slug}`,
        startDate: `${date}T${startTime}${TULUM_TIMEZONE_OFFSET}`,
        location: location(title),
      };
    }).filter(event => event !== null); // Filter out any null entries from missing data
  }

  // Generates an array of social media links
  function generateSocialLinks() {
    const links = [];
    const instagramUrl = getSafe(() => artist.metadata.instagram);
    const spotifyId = getSafe(() => artist.metadata.spotify_id);
    const soundcloudUrl = getSafe(() => artist.metadata.soundcloud);

    if (instagramUrl) {
      links.push(instagramUrl);
    }
    if (spotifyId) {
      links.push(`${SPOTIFY_ARTIST_BASE_URL}${spotifyId}`);
    }
    if (soundcloudUrl) {
      links.push(soundcloudUrl);
    }
    return links.length > 0 ? links : undefined;
  }

  // Parses and formats genre strings
  function parseGenres() {
    // Prefer 'genre' but fallback to 'genere' if that's the actual data field
    const genreString = getSafe(() => artist.metadata.genere );
    if (!genreString || typeof genreString !== 'string') {
      return undefined;
    }
    // More robustly split by common delimiters: ',', '|', '&', 'and'
    // and handle potential extra spaces around them.
    return genreString
      .split(/,|\/|\||&|\band\b/i) // Split by comma, slash, pipe, ampersand, or the word "and" (case-insensitive)
      .map(g => g.trim())
      .filter(g => g.length > 0); // Remove any empty strings resulting from multiple delimiters
  }

  const mainImageUrl = getSafe(() => artist.metadata.main_image.imgix_url);
  const artistSlug = getSafe(() => artist.slug);
  const artistTitle = getSafe(() => artist.title);

  if (!artistSlug || !artistTitle) {
    console.error("Artist slug or title is missing, cannot generate schema.", artist);
    return null; // Or throw an error
  }

  return {
    "@context": "https://schema.org",
    "@id": `${TULUM_PARTY_BASE_URL}/dj/${artistSlug}#entity`,
    "@type": "MusicGroup",
    name: artistTitle,
    description: cleanContent(),
    url: `${TULUM_PARTY_BASE_URL}/dj/${artistSlug}`,
    image: mainImageUrl ? [mainImageUrl] : undefined, // Only add image array if URL exists
    genre: parseGenres(),
    event: generateEventData(),
    sameAs: generateSocialLinks(),
  };
}
